import React, { useEffect, useState } from "react";
import useSWR, { mutate } from "swr";
import { config } from "~/core";
import Table from "../components/tables/Table";
import InspectionPreAddNoteModal from "../components/Modal/InspectionPreAddNote";
import { apiProvider } from "../api/core/provider";
import EditInspectionPriceModal from "../components/Modal/EditInspectionPrice";
import axios from "axios";
import { inspections } from "../api/inspections";
import formatMoney from "../utilities/formatMoney";

export default function InspectionPrices() {

  const BASE_URL = "/api/" + config.api.version;

  const editPrice = type => {
    setEditingInspectionType(type);
    setEditPriceOpen(true);
  };

  const editDefaultInspectorPrice = type => {
    setEditingDefaultInspectorType(type);
    setEditDefaultInspectorPriceOpen(true);
  };

  const editCondoPrice = type => {
    setEditingCondoType(type);
    setEditCondoPriceOpen(true);
  };

  const editCondoDefaultInspectorPrice = type => {
    setEditingCondoDefaultInspectorType(type);
    setEditCondoDefaultInspectorPriceOpen(true);
  };

  const savePriceHandler = (inspectionType, newPrice, updateAll, effectiveDate = null, condo = false) => {
    const payload = {
      type: inspectionType.ID,
      price: Number(newPrice*100),
      updateAll: updateAll,
      condo: condo
    };

    // Add effective date if provided
    if (effectiveDate) {
      payload.effectiveDate = effectiveDate;
    }

    apiProvider.post(`accounting/prices`, payload).then((data) => {
      mutate(`${BASE_URL}/accounting/prices`);
    }
    ).catch((e) => {
      console.log("Error saving price - ",e);
    });
  };

  const saveDefaultInspectorPriceHandler = (inspectionType, newPrice, updateAll, effectiveDate = null, condo = false) => {
    const payload = {
      type: inspectionType.ID,
      price: Number(newPrice*100),
      updateAll: updateAll,
      condo: condo
    };

    // Add effective date if provided
    if (effectiveDate) {
      payload.effectiveDate = effectiveDate;
    }

    apiProvider.post(`accounting/prices/default-inspector`, payload).then((data) => {
      mutate(`${BASE_URL}/accounting/prices/default-inspector`);
    }
    ).catch((e) => {
      console.log("Error saving default inspector price - ",e);
    });
  };

  const [editingInspectionType, setEditingInspectionType] = useState(null);
  const [editPriceOpen, setEditPriceOpen] = useState(false);
  const [editingDefaultInspectorType, setEditingDefaultInspectorType] = useState(null);
  const [editDefaultInspectorPriceOpen, setEditDefaultInspectorPriceOpen] = useState(false);
  const [editingCondoType, setEditingCondoType] = useState(null);
  const [editCondoPriceOpen, setEditCondoPriceOpen] = useState(false);
  const [editingCondoDefaultInspectorType, setEditingCondoDefaultInspectorType] = useState(null);
  const [editCondoDefaultInspectorPriceOpen, setEditCondoDefaultInspectorPriceOpen] = useState(false);
  const [priceTable, setPriceTable] = useState(null);

  const cols = [
    { Header: "Inspection Type", accessor: "InspectionType" },
    { Header: "Inspection Price", accessor: "InspectionPriceWithEdit" },
    { Header: "Condo Price", accessor: "CondoPriceWithEdit" },
    { Header: "Default Inspector Price", accessor: "DefaultInspectorPriceWithEdit" },
    { Header: "Default Inspector Condo Price", accessor: "DefaultInspectorCondoPriceWithEdit" },
  ];

  const { data: companies } = useSWR(`${BASE_URL}/companies`);
  const { data: inspectionTypePrices } = useSWR(`${BASE_URL}/accounting/prices`);
  const { data: defaultInspectorPrices } = useSWR(`${BASE_URL}/accounting/prices/default-inspector`);

  useEffect(() => {
    if (!inspectionTypePrices || !inspectionTypePrices.result) {
      return;
    }
    
    let prices = JSON.parse(JSON.stringify(inspectionTypePrices.result));
    let defaultPrices = {};
    
    // Create a map of default inspector prices by inspection type ID
    if (defaultInspectorPrices && defaultInspectorPrices.result) {
      defaultInspectorPrices.result.forEach(item => {
        // The API returns InspectionTypeDefaultInspectorPrice objects
        // Use InspectionTypeId for the key and Price for the value
        // Condo prices are keyed separately
        defaultPrices[item.Condo ? `condo-${item.InspectionTypeId}` : item.InspectionTypeId] = item.Price;
      });
    }
    
    prices.sort((a,b) => {
      if (a.InspectionType < b.InspectionType) {
        return -1;
      }
      if (a.InspectionType > b.InspectionType) {
        return 1;
      }
      return 0;
    });
    
    let formattedPrices = prices.map(inspectionType => {
      // Format inspection price
      if (Number(inspectionType.Price) > 0) {
        inspectionType.FormattedPrice = formatMoney(Number(inspectionType.Price) / 100);
        inspectionType.Price = Number(inspectionType.Price) / 100;
      } else {
        inspectionType.FormattedPrice = 0;
      }

      // Format condo price (0 = not configured, condos bill the regular price)
      const condoPrice = Number(inspectionType.CondoPrice) || 0;
      inspectionType.FormattedCondoPrice = condoPrice > 0 ? formatMoney(condoPrice / 100) : "";
      inspectionType.CondoPrice = condoPrice / 100;

      // Format default inspector price
      const defaultPrice = defaultPrices[inspectionType.ID] || 0;
      if (Number(defaultPrice) > 0) {
        inspectionType.FormattedDefaultInspectorPrice = formatMoney(Number(defaultPrice) / 100);
        inspectionType.DefaultInspectorPrice = Number(defaultPrice) / 100;
      } else {
        inspectionType.FormattedDefaultInspectorPrice = 0;
        inspectionType.DefaultInspectorPrice = 0;
      }

      // Format default inspector condo price
      const defaultCondoPrice = defaultPrices[`condo-${inspectionType.ID}`] || 0;
      inspectionType.FormattedDefaultInspectorCondoPrice = defaultCondoPrice > 0 ? formatMoney(defaultCondoPrice / 100) : "";
      inspectionType.DefaultInspectorCondoPrice = defaultCondoPrice / 100;
      
      // Create inspection price with edit button
      inspectionType.InspectionPriceWithEdit = 
        <div className="tw-flex tw-justify-between tw-items-center tw-w-full" style={{ width: 100 }}>
          <span className="tw-block tw-min-w-[32px]">{inspectionType.FormattedPrice}</span>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => editPrice(inspectionType)}
          >
            Edit
          </button>
        </div>
      ;

      // Create condo price with edit button
      inspectionType.CondoPriceWithEdit =
        <div className="tw-flex tw-justify-between tw-items-center tw-w-full" style={{ width: 100 }}>
          <span className="tw-block tw-min-w-[32px]">{inspectionType.FormattedCondoPrice}</span>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => editCondoPrice(inspectionType)}
          >
            Edit
          </button>
        </div>
      ;

      // Create default inspector price with edit button
      inspectionType.DefaultInspectorPriceWithEdit = 
        <div className="tw-flex tw-justify-between tw-items-center tw-w-full" style={{ width: 100 }}>
          <span className="tw-block tw-min-w-[32px]">{inspectionType.FormattedDefaultInspectorPrice}</span>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => editDefaultInspectorPrice(inspectionType)}
          >
            Edit
          </button>
        </div>
      ;

      // Create default inspector condo price with edit button
      inspectionType.DefaultInspectorCondoPriceWithEdit =
        <div className="tw-flex tw-justify-between tw-items-center tw-w-full" style={{ width: 100 }}>
          <span className="tw-block tw-min-w-[32px]">{inspectionType.FormattedDefaultInspectorCondoPrice}</span>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => editCondoDefaultInspectorPrice(inspectionType)}
          >
            Edit
          </button>
        </div>
      ;

      return inspectionType;
    });
    setPriceTable(formattedPrices);
  }, [inspectionTypePrices, defaultInspectorPrices]);

  return (
    <>
      <h1>Inspection Prices</h1>

      <div className="tw-mb-4 tw-text-sm tw-text-gray-600">
        Manage inspection prices and default inspector prices. Default inspector prices are used when an inspector doesn&apos;t have a specific price set for an inspection type.
      </div>

      {priceTable &&
            <div className={"tw-mt-14 tw-mb-10"}>
              <Table data={priceTable} columns={cols}/>
            </div>
      }

      <EditInspectionPriceModal inspectionType={editingInspectionType} open={editPriceOpen}
        closeHandler={() => setEditPriceOpen(false)}
        saveHandler={savePriceHandler}/>

      <EditInspectionPriceModal 
        inspectionType={editingDefaultInspectorType} 
        open={editDefaultInspectorPriceOpen}
        closeHandler={() => setEditDefaultInspectorPriceOpen(false)}
        saveHandler={saveDefaultInspectorPriceHandler}
        priceField="DefaultInspectorPrice"
        title="Edit Default Inspector Price for"
        initialPrice={editingDefaultInspectorType?.DefaultInspectorPrice || 0}
      />

      <EditInspectionPriceModal
        inspectionType={editingCondoType}
        open={editCondoPriceOpen}
        closeHandler={() => setEditCondoPriceOpen(false)}
        saveHandler={(type, price, updateAll, date) => savePriceHandler(type, price, updateAll, date, true)}
        priceField="CondoPrice"
        title="Edit Condo Price for"
        initialPrice={editingCondoType?.CondoPrice || 0}
      />

      <EditInspectionPriceModal
        inspectionType={editingCondoDefaultInspectorType}
        open={editCondoDefaultInspectorPriceOpen}
        closeHandler={() => setEditCondoDefaultInspectorPriceOpen(false)}
        saveHandler={(type, price, updateAll, date) => saveDefaultInspectorPriceHandler(type, price, updateAll, date, true)}
        priceField="DefaultInspectorCondoPrice"
        title="Edit Default Inspector Condo Price for"
        initialPrice={editingCondoDefaultInspectorType?.DefaultInspectorCondoPrice || 0}
      />

    </>
  );
}